/**
 * FirstRunBootstrapCard — single-card welcome screen rendered when
 * the preflight state machine returns 'first-run'.
 *
 * Replaces the multi-panel preflight wall (which incorrectly told
 * users that every section was "missing" — i.e. instructed them to
 * do exactly what they were trying to do). Instead, this card frames
 * the first run as evidence collection, with optional context and explicit
 * screening limitations. It does not promise a fixed runtime or confidence uplift.
 *
 * Visual treatment is intentionally welcoming, not anxious:
 *   - subtle blue tint (`bg-info/5`), no red X / blocked / percent.
 *   - sparkles icon + active verb in the title.
 *   - section preview is descriptive, not a checklist.
 *   - cost line uses small uppercase body text — info, not warning.
 *   - footer note tells the user this card only shows on the first run.
 *
 * Doc upload is offered as a light optional affordance, not the
 * focus — first-run users typically don't have docs yet, and the
 * core value proposition is "we collect everything for you."
 *
 * NWX-6 (2026-05-18) — SSOT inheritance block. Originally the
 * bare welcome card ignored every site-level config layer
 * (projectIntent / typedPreferencesDefaults / siteContext /
 * evidenceMode) so the user couldn't tell which intent was about
 * to drive their report. Now the card surfaces a four-row
 * inheritance section sourced directly from the SiteEntity,
 * with deep-link affordances back to the Overview tab and a
 * "{N} of 4 site-level config sections still need attention"
 * hint when the user has fewer than 4 set.
 */
import React from 'react';
import { Sparkles, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { useIsDatacrateAdmin } from '../../lib/isDatacrateAdmin';
import type { SiteEntity } from '../../types/site';
import { type ReportTypeId, REPORT_TYPE_REGISTRY, getRequiredSedSectionsFor } from '../../config/reportTypes';
import { allowsScreeningWithSourceGaps } from '../../lib/reportGeneration/screeningPolicy';
import { SED_SECTION_LABELS } from '../../lib/sedStaleness';
import type { SedSectionId } from '../../types/siteEvidence';
import { effectivePower } from '../../lib/projectIntent';
import { normaliseCooling, COOLING_TYPE_LABEL } from '../../types/cooling';

interface Props {
  site: SiteEntity;
  reportType: ReportTypeId;
  onGenerate: () => void;
  onOpenDocLibrary: () => void;
  docCount: number;
  /** NWX-6 — optional deep-link handler. Studio mounts pass this so
   *  the inherited-config rows can route the user back to the
   *  Overview tab section that backs the row. When omitted the rows
   *  render as plain text (no router dependency in the card). */
  onEditAtSiteLevel?: (anchor: 'projectIntent' | 'reportPreferences' | 'siteContext' | 'evidenceMode') => void;
}

// Estimated cost knobs. Aligned with EST_PROVIDER_COST_USD in
// sedStaleness.ts (per-provider) and the empirical generation-cost
// average. These are rough — the cost line is framed as an estimate.
const EST_PROVIDER_COST_USD = 0.02;
const EST_GENERATION_COST_USD = 0.12;

const SECTION_PREVIEW_COUNT = 4;

export function FirstRunBootstrapCard({
  site,
  reportType,
  onGenerate,
  onOpenDocLibrary,
  docCount,
  onEditAtSiteLevel,
}: Props) {
  const required = getRequiredSedSectionsFor(reportType);
  const reportLabel = REPORT_TYPE_REGISTRY[reportType]?.label ?? reportType;
  const providerCost = (required.length * EST_PROVIDER_COST_USD).toFixed(2);
  const totalCost = (required.length * EST_PROVIDER_COST_USD + EST_GENERATION_COST_USD).toFixed(2);
  const sectionPreview = required
    .slice(0, SECTION_PREVIEW_COUNT)
    .map(s => SED_SECTION_LABELS[s as SedSectionId] ?? s)
    .join(' · ');
  const moreCount = Math.max(0, required.length - SECTION_PREVIEW_COUNT);

  // Cost visibility is gated to Datacrate admins only (platform
  // super-admin OR root-company admin — see `isDatacrateAdmin`). The
  // same predicate gates the RefreshEvidencePanel cost line. Company
  // admins / regular tenant users see NO cost figure. The cost MATH
  // below still runs; only the rendered $ is hidden.
  const isDatacrateAdmin = useIsDatacrateAdmin();

  // NWX-6 — derive the four SSOT-inherited row strings. Each row
  // computes a `set` boolean so we can show the user how many of
  // the four config layers are populated. Set thresholds:
  //   - projectIntent — at least one non-undecided meaningful field
  //   - reportPreferences (typedPreferencesDefaults for this type)
  //     — at least one populated value
  //   - siteContext — at least one claim
  //   - evidenceMode — explicitly set (not awaiting / undefined)
  // Location-screening framing ("the coordinates are enough to begin"): every
  // Stage 1 type, plus a location-screening family outside both stages
  // (Public Sentiment, an additional analysis since 2026-09-30). A Stage 2
  // type — Environmental included since that date — keeps the "prerequisites
  // still apply" framing: it waits on the promoted generation scenario.
  const stage = REPORT_TYPE_REGISTRY[reportType]?.stage;
  const isScreening = stage === 1 || (stage == null && allowsScreeningWithSourceGaps(reportType));
  const intent = site.projectIntent;
  const intentEffPower = effectivePower(intent);
  const intentEffCooling = (() => {
    const canonical = normaliseCooling(intent?.coolingStrategy);
    return canonical === 'undecided' ? undefined : COOLING_TYPE_LABEL[canonical];
  })();
  const intentSet = Boolean(
    intent && (
      intent.workloadProfile ||
      intentEffPower.isExact ||
      (intentEffPower.bucket !== 'undecided') ||
      intentEffCooling
    ),
  );
  const intentStatus = intentSet
    ? [
        intent?.workloadProfile,
        intentEffPower.bucket !== 'undecided' || intentEffPower.isExact ? intentEffPower.displayLabel : null,
        intentEffCooling,
        intent?.timeline,
      ].filter(Boolean).join(' · ') || 'Set'
    : isScreening ? 'Not declared — optional for location screening' : 'Not declared';

  const reportPrefsBag = site.typedPreferencesDefaults?.[reportType] as Record<string, unknown> | undefined;
  const prefsSet = Boolean(reportPrefsBag && Object.keys(reportPrefsBag).length > 0);
  const prefsStatus = prefsSet ? 'Customized' : 'Using defaults';

  const contextClaimCount = site.siteContext?.claims?.length ?? 0;
  const contextSet = contextClaimCount > 0;
  const contextStatus = contextSet
    ? `${contextClaimCount} claim${contextClaimCount === 1 ? '' : 's'}`
    : 'No context yet — add source facts when available';

  const evidenceMode = site.evidenceMode;
  const evidenceModeSet = Boolean(evidenceMode && evidenceMode !== 'awaiting');
  const evidenceModeStatus = (() => {
    if (evidenceMode === 'awaiting') return 'Awaiting';
    if (evidenceMode === 'ai-only') return 'Datacrate Engine';
    if (evidenceMode === 'docs-included') return 'Documents-included';
    return 'Datacrate Engine';
  })();

  const fieldsSetCount = [intentSet, prefsSet, contextSet, evidenceModeSet]
    .filter(Boolean).length;

  // Inheritance row renderer — when `onEditAtSiteLevel` is wired,
  // each row carries an "Edit at site level →" button. When omitted,
  // the row renders as static text. This keeps the card mountable
  // in tests / Storybook without a router dependency.
  const renderRow = (
    label: string,
    status: string,
    isSet: boolean,
    anchor: 'projectIntent' | 'reportPreferences' | 'siteContext' | 'evidenceMode',
  ): React.ReactElement => (
    <div
      className="flex items-baseline justify-between gap-3"
      data-testid={`welcome-inherited-row-${anchor}`}
    >
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="type-section text-text-muted shrink-0">
          {label}
        </span>
        <span
          className={`text-xs truncate ${isSet ? 'text-text-primary' : 'text-text-muted'}`}
          data-set={isSet ? 'true' : 'false'}
        >
          {status}
        </span>
      </div>
      {onEditAtSiteLevel && (
        <button
          type="button"
          onClick={() => onEditAtSiteLevel(anchor)}
          className="type-meta text-primary hover:text-primary/80 underline shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
          data-testid={`welcome-inherited-edit-${anchor}`}
          aria-label={`Edit ${label} at site level`}
        >
          Edit
        </button>
      )}
    </div>
  );

  return (
    <div
      className="border border-border bg-info/5 p-5 space-y-4 rounded-md"
      data-testid="first-run-card"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="w-5 h-5 text-primary mt-0.5 shrink-0" aria-hidden="true" />
        <div className="space-y-1">
          <h3 className="text-sm font-bold text-text-primary">
            Generate your first {reportLabel.toLowerCase()}
          </h3>
          <p className="text-xs text-text-secondary leading-relaxed">
            {isScreening ? 'The site coordinates are enough to begin. We collect available public evidence automatically: ' : 'Public evidence is collected alongside the project inputs required for this analysis: '}
            <span className="font-mono text-text-primary">{sectionPreview}</span>
            {moreCount > 0 && (
              <span className="text-text-muted"> · {moreCount} more</span>
            )}
            .
          </p>
          {isScreening && <p className="text-xs text-text-secondary" data-testid="first-run-screening-scope">{site.studyArea ? 'A reviewed study selection defines the analysis footprint. Gross polygon area does not establish usable land or campus fit.' : 'This is a location screen. It does not establish utility headroom, parcel fit or a supported campus capacity.'} Undeclared design choices remain open; findings can guide the next investigation.</p>}
        </div>
      </div>

      {/* NWX-6 — Configuration inherited from site. Sourced directly
           from the SiteEntity (projectIntent / typedPreferencesDefaults /
           siteContext / evidenceMode); editable via the optional
           `onEditAtSiteLevel` deep-link affordance. */}
      <details
        className="border border-border bg-background p-3 rounded-md"
        data-testid="welcome-inherited-config"
      >
        <summary className="cursor-pointer list-none type-section text-text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2">
          Optional context for a stronger report
          <span className="ml-2 font-normal text-text-muted">{fieldsSetCount}/4 available</span>
        </summary>
        <div className="mt-3 space-y-1.5 border-t border-border-subtle pt-3">
          {renderRow('Project Intent', intentStatus, intentSet, 'projectIntent')}
          {renderRow('Report Preferences', prefsStatus, prefsSet, 'reportPreferences')}
          {renderRow('Site Context', contextStatus, contextSet, 'siteContext')}
          {renderRow('Evidence Mode', evidenceModeStatus, evidenceModeSet, 'evidenceMode')}
          {fieldsSetCount < 4 && (
            <p
              className="pt-1 text-[10px] italic text-text-muted"
              data-testid="welcome-inherited-hint"
            >
              {4 - fieldsSetCount} optional context item{4 - fieldsSetCount === 1 ? '' : 's'} not set. {isScreening ? 'You can still generate now.' : 'The scenario, model and evidence prerequisites for this analysis still apply.'}
            </p>
          )}
        </div>
      </details>

      {/* Optional doc upload — light affordance, not the focus. */}
      <div className="border border-dashed border-border bg-background p-3 rounded-md">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Upload className="w-3.5 h-3.5" aria-hidden="true" />
            {docCount === 0 ? (
              <span>Optional: upload supporting documents to strengthen this report</span>
            ) : (
              <span>
                <span className="text-text-primary font-bold">{docCount}</span>{' '}
                {docCount === 1 ? 'document' : 'documents'} ready · adding more is optional
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onOpenDocLibrary}
            className="type-meta text-primary hover:text-primary/80 underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1"
            data-testid="first-run-doc-affordance"
          >
            {docCount === 0 ? 'Upload' : 'Manage'}
          </button>
        </div>
      </div>

      {/* Cost estimate — Datacrate admins only. Company admins /
           regular tenant users see no cost. */}
      {isDatacrateAdmin && (
        <div className="flex items-center justify-between type-meta text-text-muted">
          <span>Estimated cost</span>
          <span className="font-mono text-text-secondary" data-testid="first-run-cost">
            ~${totalCost} (~${providerCost} providers + ~${EST_GENERATION_COST_USD.toFixed(2)} generation)
          </span>
        </div>
      )}

      <Button onClick={onGenerate} className="w-full h-9" data-testid="first-run-generate">
        Generate report
      </Button>

      <p className="text-[10px] text-text-muted text-center">
        {isScreening ? 'Missing private or site-specific evidence is a disclosed limitation, not a generation gate. Add sources or refine the project intent later, then refresh the analyses that use them.' : 'Complete the required engineering or financial inputs for this analysis. Public-source evidence does not replace a required scenario or model.'}
      </p>
    </div>
  );
}
